import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, FileText, AlertTriangle, Scale, Users, Ban, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import BirdMascot from '../components/BirdMascot';
import Footer from '../components/Footer';

const TermsOfServicePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = 'December 18, 2025';

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-muted/30 to-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <BirdMascot size={32} />
            <span className="text-xl font-heading font-bold">JailBirds</span>
          </Link>
          
          <Link to="/">
            <Button variant="outline" size="sm" className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Hero Section */}
          <div className="text-center mb-8">
            <FileText className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4 text-gradient-primary">
              Terms of Service
            </h1>
            <p className="text-muted-foreground">
              Last Updated: {lastUpdated}
            </p>
          </div>

          {/* Agreement */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Agreement to Terms</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Welcome to JailBirds! These Terms of Service ("Terms") govern your access to and use of the JailBirds website at jailbirdsgame.com (the "Site") and our guessing game service (the "Game").
              </p>
              <p>
                By accessing or using our Site, you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not access the Site or use the Game.
              </p>
            </div>
          </div>

          {/* Eligibility */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-bold">Eligibility</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                You must be at least 18 years old to use this Site and play the Game. The Game contains content related to criminal arrests and may not be suitable for minors. By using the Site, you represent and warrant that you are at least 18 years of age.
              </p>
            </div>
          </div>

          {/* Description of Service */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Description of Service</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                JailBirds is an entertainment-based guessing game that presents players with publicly available mugshot images and criminal charge information from Mecklenburg County, North Carolina. Players attempt to correctly match individuals with their associated charges.
              </p>
              <p>
                The Game includes:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Multiple-choice questions based on real arrest records</li>
                <li>A scoring system with points for correct answers</li>
                <li>Leaderboards displaying top player scores</li>
                <li>Optional nickname registration for leaderboard placement</li>
                <li>Daily updated content from public records</li>
              </ul>
            </div>
          </div>

          {/* User Conduct */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Ban className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-bold">User Conduct</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>When using our Site, you agree NOT to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Use offensive, vulgar, or inappropriate nicknames</li>
                <li>Attempt to manipulate scores or game outcomes through cheating</li>
                <li>Use automated tools, bots, or scripts to play the Game</li>
                <li>Harass, abuse, or threaten other users</li>
                <li>Attempt to access unauthorized areas of the Site</li>
                <li>Interfere with the proper functioning of the Site</li>
                <li>Use the Site for any illegal purpose</li>
                <li>Scrape or collect data from the Site without permission</li>
                <li>Impersonate any person or entity</li>
              </ul>
              <p className="mt-4">
                We reserve the right to remove any content and terminate access for users who violate these guidelines.
              </p>
            </div>
          </div>

          {/* Public Records Disclaimer */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-yellow-500" />
              <h2 className="text-2xl font-heading font-bold">Public Records Disclaimer</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                All mugshot images and arrest information displayed in the Game are obtained from publicly available records maintained by the Mecklenburg County Sheriff's Office. This information is in the public domain and is provided for entertainment purposes only.
              </p>
              <p className="font-semibold text-foreground">
                Important notices:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>An arrest does not imply guilt. All individuals are presumed innocent until proven guilty in a court of law.</li>
                <li>Charges may be reduced, dismissed, or result in acquittal after the arrest date.</li>
                <li>We do not verify the accuracy of records provided by law enforcement.</li>
                <li>Information may be outdated or contain errors from the source.</li>
              </ul>
            </div>
          </div>

          {/* Intellectual Property */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Intellectual Property</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                The Site and its original content (excluding public records data), features, and functionality are owned by JailBirds and are protected by international copyright, trademark, and other intellectual property laws.
              </p>
              <p>
                The JailBirds name, logo, and bird mascot are trademarks of JailBirds. You may not use these marks without our prior written permission.
              </p>
            </div>
          </div>

          {/* Disclaimer of Warranties */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Scale className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-bold">Disclaimer of Warranties</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p className="uppercase font-semibold">
                THE SITE AND GAME ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.
              </p>
              <p>
                We do not warrant that:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>The Site will be available at all times without interruption</li>
                <li>The information provided is accurate, complete, or current</li>
                <li>The Site is free of viruses or harmful components</li>
                <li>Any errors or defects will be corrected</li>
              </ul>
            </div>
          </div>

          {/* Limitation of Liability */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Limitation of Liability</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, JAILBIRDS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR OTHER INTANGIBLE LOSSES.
              </p>
              <p>
                Our total liability for any claims arising from your use of the Site shall not exceed the amount you paid to us (if any) in the twelve months preceding the claim.
              </p>
            </div>
          </div>

          {/* Indemnification */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Indemnification</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                You agree to indemnify and hold harmless JailBirds, its operators, and affiliates from any claims, damages, losses, or expenses (including legal fees) arising from your use of the Site, violation of these Terms, or infringement of any rights of another party.
              </p>
            </div>
          </div>

          {/* Termination */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Termination</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                We may terminate or suspend your access to the Site immediately, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, your right to use the Site will cease immediately.
              </p>
            </div>
          </div>

          {/* Governing Law */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Governing Law</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of North Carolina, United States, without regard to its conflict of law provisions.
              </p>
            </div>
          </div>

          {/* Changes to Terms */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Changes to Terms</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                We reserve the right to modify or replace these Terms at any time. If we make material changes, we will provide notice by updating the "Last Updated" date. Your continued use of the Site after any changes constitutes acceptance of the new Terms.
              </p>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-bold">Contact Us</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>If you have any questions about these Terms, please contact us:</p>
              <ul className="space-y-2 ml-4">
                <li><strong>Email:</strong> <a href="mailto:legal@jailbirdsgame.com" className="text-primary hover:underline">legal@jailbirdsgame.com</a></li>
                <li><strong>Website:</strong> <a href="https://jailbirdsgame.com/contact" className="text-primary hover:underline">jailbirdsgame.com/contact</a></li>
              </ul>
            </div>
          </div>

        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;